import React, { useMemo } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const TREND_COLORS = {
  sales: "#5D38DB",
  collections: "#0D9488",
};

const LEADERBOARD_COLORS = ["#5D38DB", "#2563EB", "#0D9488", "#D97706", "#7C3AED"];
const CHANNEL_COLORS = ["#5D38DB", "#2563EB", "#D97706", "#7C3AED", "#64748B"];

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const LeaderboardTooltip = ({ active, payload, metricLabel }) => {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{row.name}</p>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">{metricLabel}</span>
        <span className="owner-chart-tooltip__value">
          {formatCurrency(row.value)}
        </span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Outstanding</span>
        <span className="owner-chart-tooltip__value">
          {formatCurrency(row.outstanding)}
        </span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Credit limit</span>
        <span className="owner-chart-tooltip__value">
          {formatCurrency(row.credit_limit)}
        </span>
      </div>
    </div>
  );
};

const LeaderboardChart = ({ data, color, metricLabel, emptyMessage }) => {
  if (!data.length) {
    return (
      <div className="owner-chart-empty owner-chart-empty--compact">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(260, data.length * 46)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={formatCompact}
          tick={{ fill: "#64748b", fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={120}
          tick={{ fill: "#334155", fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<LeaderboardTooltip metricLabel={metricLabel} />} />
        <Bar dataKey="value" name={metricLabel} radius={[0, 6, 6, 0]} barSize={22}>
          {data.map((entry, index) => (
            <Cell
              key={entry.customer_id || entry.name}
              fill={index === 0 ? color : LEADERBOARD_COLORS[index % LEADERBOARD_COLORS.length]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const OwnerCustomerInsightsChart = ({
  data = [],
  series = [],
  summary = {},
  topBySales = [],
  topByOutstanding = [],
  byChannel = [],
  overLimit = [],
  loading = false,
}) => {
  const channelData = useMemo(
    () =>
      byChannel
        .filter((item) => item.amount > 0)
        .map((item, index) => ({
          name: item.channel,
          value: item.amount,
          pct: item.pct,
          fill: CHANNEL_COLORS[index % CHANNEL_COLORS.length],
        })),
    [byChannel]
  );

  const salesSeries = useMemo(
    () => series.find((item) => item.key === "Sales"),
    [series]
  );
  const collectionsSeries = useMemo(
    () => series.find((item) => item.key === "Collections"),
    [series]
  );

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (
    !data.length &&
    !summary?.active_customers &&
    !summary?.total_receivables
  ) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-users owner-chart-empty__icon" aria-hidden="true" />
        <p>No customer credit data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Total receivables</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.total_receivables)}
            </strong>
            <small className="owner-sales-kpi__hint">
              {summary?.active_customers || 0} credit customers
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Period credit sales</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.period_credit_sales)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--teal">
            <span className="owner-sales-kpi__label">Collections</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.period_collections)}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatPercent(summary?.collection_rate)} of sales
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Top customer (sales)</span>
            <strong className="owner-sales-kpi__value">
              {summary?.top_customer || "—"}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.top_customer_sales)}
            </small>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">
              Credit sales vs collections
            </p>
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<TrendTooltip />} />
                <Legend />

                {salesSeries ? (
                  <Area
                    type="monotone"
                    dataKey={salesSeries.key}
                    name={salesSeries.label}
                    stroke={TREND_COLORS.sales}
                    fill={TREND_COLORS.sales}
                    fillOpacity={0.25}
                    strokeWidth={2}
                    connectNulls
                  />
                ) : null}

                {collectionsSeries ? (
                  <Line
                    type="monotone"
                    dataKey={collectionsSeries.key}
                    name={collectionsSeries.label}
                    stroke={TREND_COLORS.collections}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: TREND_COLORS.collections, strokeWidth: 0 }}
                    connectNulls
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="owner-chart-panel owner-chart-panel--compact h-100">
            <p className="owner-chart-panel__subtitle mb-2">Sales by channel</p>
            {channelData.length ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={channelData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={88}
                      paddingAngle={2}
                    >
                      {channelData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        formatCurrency(value),
                        `${name} (${formatPercent(props?.payload?.pct)})`,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="owner-chart-legend mt-2">
                  {channelData.map((item) => (
                    <div className="owner-chart-legend__item" key={item.name}>
                      <span
                        className="owner-chart-legend__swatch"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span>
                        {item.name} ({formatPercent(item.pct)})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="owner-chart-empty owner-chart-empty--compact">
                <p>No channel split for this period.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Top customers by sales</p>
            <LeaderboardChart
              data={topBySales}
              color={TREND_COLORS.sales}
              metricLabel="Period sales"
              emptyMessage="No credit sales in this period."
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Top customers by outstanding</p>
            <LeaderboardChart
              data={topByOutstanding}
              color="#D97706"
              metricLabel="Outstanding"
              emptyMessage="No outstanding balances."
            />
          </div>
        </div>
      </div>

      {overLimit.length > 0 ? (
        <div className="owner-chart-panel">
          <p className="owner-chart-panel__subtitle mb-2">
            Over credit limit ({overLimit.length})
          </p>
          <div className="table-responsive">
            <table className="table table-sm owner-employee-table mb-0">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th className="text-end">Outstanding</th>
                  <th className="text-end">Credit limit</th>
                  <th className="text-end">Over by</th>
                </tr>
              </thead>
              <tbody>
                {overLimit.map((row) => (
                  <tr key={row.customer_id}>
                    <td>{row.name}</td>
                    <td className="text-end">{formatCurrency(row.outstanding)}</td>
                    <td className="text-end">{formatCurrency(row.credit_limit)}</td>
                    <td className="text-end text-danger">
                      {formatCurrency(row.over_by)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OwnerCustomerInsightsChart;
